/**
 * `useSourceAssessRequestStore` — session-local registry of in-flight
 * source-assessment directives, keyed by `${caseId}::${file.path}`.
 *
 * WHY: the assessment pane's `.info` read does not poll — the evaluation
 * happens agent-side and fresh content arrives only when the file is
 * reopened. The pane's in-flight latch therefore cannot live in component
 * state: closing the viewer unmounts the pane, and a remount would re-arm
 * the trigger while the directive is still being worked (operator caught
 * exactly that, 2026-07-30). Entries carry the assessment fingerprint the
 * request was fired against, so a reopen can tell "still in flight"
 * (fingerprint unchanged → keep showing "Avaliando…") from "landed"
 * (fingerprint moved → entry cleared, fresh state renders), plus a TTL so
 * a directive lost runtime-side (a restart drops the in-memory event
 * queue — observed live 2026-07-30 20:45) re-arms the button instead of
 * locking it forever.
 *
 * Deliberately NOT persisted (no `persist`/partialize): a page reload
 * re-reads every `.info` anyway, and a marker surviving reloads would
 * have no in-flight send behind it and no way to clear itself.
 */
import { create } from "zustand";

/** Re-arm window — after this, a silently-lost directive stops latching the CTA. */
export const ASSESS_REQUEST_TTL_MS = 10 * 60_000;

export interface AssessRequestEntry {
  /** Epoch ms of the successful enqueue. */
  at: number;
  /** Assessment fingerprint the directive was fired against. */
  baseline: string;
}

interface SourceAssessRequestState {
  entries: Record<string, AssessRequestEntry>;
  mark: (key: string, baseline: string) => void;
  clear: (key: string) => void;
}

export const useSourceAssessRequestStore = create<SourceAssessRequestState>((set) => ({
  entries: {},
  mark: (key, baseline) =>
    set((s) => ({ entries: { ...s.entries, [key]: { at: Date.now(), baseline } } })),
  clear: (key) =>
    set((s) => {
      if (!(key in s.entries)) return s;
      const next = { ...s.entries };
      delete next[key];
      return { entries: next };
    }),
}));

export function assessRequestKey(caseId: string | undefined, path: string): string {
  return `${caseId ?? "?"}::${path}`;
}

/**
 * Fingerprint of the assessment the lawyer is looking at. `pending` has no
 * `assessedAt`; any status flip or a re-stamp means the evaluation landed
 * and the in-flight marker must clear.
 */
export function assessmentBaseline(a: { status: string; assessedAt?: string }): string {
  return `${a.status}|${a.assessedAt ?? ""}`;
}

/** True while the marker should keep the pane in its "Avaliando…" state. */
export function isRequestActive(
  entry: AssessRequestEntry | undefined,
  currentBaseline: string,
  now = Date.now(),
): boolean {
  return !!entry && now - entry.at < ASSESS_REQUEST_TTL_MS && entry.baseline === currentBaseline;
}
